import { z } from "zod";
import {
  CheckResult,
  CheckSeverity,
  Decision,
  IsoDateTime,
  OperatorEnvironment,
  PlayerRef,
  PlayerSessionRef,
  Product,
  Purpose,
} from "./common.ts";
import { DeviceAssessmentV1, LocationAssessmentV1, RiskAssessmentV1 } from "./assessments.ts";
import { LocationState, VerificationIdField } from "./verify.ts";

/**
 * `VerificationCompleted` v1 integration event payload (published by `core` through `core.outbox`,
 * ADR 0001 R3). Never carries coordinates, user agent, nonce, IP, tokens or PII: only pseudonymous
 * references and coarse device data. Strict: unknown fields are rejected.
 */
export const VerificationCompletedV1 = z
  .object({
    verification_id: VerificationIdField,
    operator_id: z.uuid(),
    environment: OperatorEnvironment,
    product: Product,
    purpose: Purpose,
    player_ref: PlayerRef,
    player_session_ref: PlayerSessionRef,
    device_ref: z.uuid().optional(),
    device_ref_reset: z.boolean(),
    decision: Decision,
    reasons: z.array(z.object({ code: z.string(), category: z.string() }).strict()),
    checks: z.array(
      z
        .object({
          code: z.string(),
          severity: CheckSeverity,
          required: z.boolean(),
          result: CheckResult,
          reason: z.string().optional(),
          cause: z.string().optional(),
        })
        .strict(),
    ),
    /** Only on `allow`. */
    revalidation: z
      .object({
        valid_until: IsoDateTime,
        next_check_in: z.int().positive(),
        buffer_time: z.int().nonnegative(),
      })
      .strict()
      .optional(),
    sdk: z.object({ name: z.string(), version: z.string() }).strict(),
    /**
     * Coarse device (`os`, `browser`, `browser_version`) plus the raw device signals `timezone`,
     * `os_version`, `screen` and `language`. The raw ones are absent only when the event is
     * replayed for a verification whose raw device/network tier was already reduced by retention
     * (`ReplayVerificationCompleted`): a replay never restores what retention cleared.
     */
    device: z
      .object({
        timezone: z.string().optional(),
        os: z.string(),
        os_version: z.string().optional(),
        screen: z
          .object({ width: z.int().positive(), height: z.int().positive() })
          .strict()
          .optional(),
        browser: z.string(),
        browser_version: z.string(),
        language: z.string().optional(),
      })
      .strict(),
    /** Browser location state (never the coordinates). */
    location_permission: LocationState,
    completed_at: IsoDateTime,
    // --- Optional telemetry of add-explorer-mvp (task 5.5): v1 extended, backward-compatible. ---
    response_time_ms: z.int().nonnegative().optional(),
    /** Only `ip` in v0 (180-day raw device/network tier); isp, organization, connection_type absent. */
    network: z
      .object({
        ip: z.union([z.ipv4(), z.ipv6()]).optional(),
        isp: z.string().optional(),
        organization: z.string().optional(),
        connection_type: z
          .enum(["residential", "mobile", "corporate", "hosting", "unknown"])
          .optional(),
      })
      .strict()
      .optional(),
    ip_dataset_version: z.string().min(1).optional(),
    boundary_dataset_version: z.string().min(1).optional(),
    /**
     * `strust-server` build that evaluated the verification (package version, plus `+<commit>`
     * when known; task 5.23, design D19). Absent = no information (older events or builds).
     */
    server_version: z
      .string()
      .regex(/^[0-9A-Za-z.+-]{1,64}$/)
      .optional(),
    /** Coarse location per source; never coordinates (precise ones stay encrypted in core). */
    location: z
      .object({
        primary_source: z.enum(["browser", "ip", "none"]),
        sources: z.array(
          z
            .object({
              kind: z.enum(["ip", "browser", "wifi"]),
              availability: z.enum(["available", "unavailable"]),
              unavailable_cause: z.string().optional(),
              country: z
                .string()
                .regex(/^[A-Z]{2}$/)
                .optional(),
              subdivision: z
                .string()
                .regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/)
                .optional(),
              municipality: z.string().optional(),
              /**
               * Name of the municipality from the GeoBoundaryDataset (e.g. "Rio de Janeiro"),
               * only with `municipality`; absent in older events (owner decision 2026-10-01).
               */
              municipality_name: z.string().min(1).max(120).optional(),
              postal_code: z.string().optional(),
              geohash5: z
                .string()
                .regex(/^[0-9b-hjkmnp-z]{5}$/)
                .optional(),
              accuracy_km: z.number().nonnegative().optional(),
              border_uncertain: z.boolean().optional(),
            })
            .strict(),
        ),
      })
      .strict()
      .optional(),
    /** Fields a browser cannot provide (`mac`, `ssid`, `bssid`). */
    absent_fields: z.array(z.object({ field: z.string(), cause: z.string() }).strict()).optional(),
    /** Block status of the player and the device at the time of the transaction. */
    player_status_at: z.enum(["none", "blocked"]).optional(),
    device_status_at: z.enum(["none", "blocked"]).optional(),
  })
  .strict()
  .meta({ id: "VerificationCompletedV1" });
export type VerificationCompletedV1 = z.infer<typeof VerificationCompletedV1>;

/** Event type of {@link DecisionMadeV1} on the bus and in the outbox. */
export const DECISION_MADE_EVENT = "DecisionMade" as const;
export const DECISION_MADE_EVENT_VERSION = 1 as const;

/**
 * `DecisionMade` v1 integration event payload (change split-core-into-trust-contexts, design D6;
 * ADR 0017 as amended by revise-event-driven-architecture, design D2/D5): exactly one per
 * Interaction, written by the Decision Engine to its outbox (schema `core`) in the transaction of
 * the verification record; replaces `VerificationCompleted`. It carries every field of
 * {@link VerificationCompletedV1} unchanged (same optional telemetry, so a consumer that accepts
 * both events projects them with one mapping) plus `assessments`: the Location, Device and Risk
 * assessments the decision was taken from (top-level `location` and `absent_fields` equal
 * `assessments.location`'s `primary_source`/`sources`/`absent_fields`). Never precise coordinates,
 * the `verdict_token` or PII. Strict producer schema: unknown fields are rejected (tolerant
 * consumer schemas come with the Kafka transport). No `aggregate_version` in this change.
 */
export const DecisionMadeV1 = VerificationCompletedV1.extend({
  assessments: z
    .object({
      location: LocationAssessmentV1,
      device: DeviceAssessmentV1,
      risk: RiskAssessmentV1,
    })
    .strict(),
})
  .strict()
  .superRefine((event, ctx) => {
    const device = event.assessments.device;
    if (
      device.device_ref !== event.device_ref ||
      device.device_ref_reset !== event.device_ref_reset
    )
      ctx.addIssue({
        code: "custom",
        path: ["assessments", "device"],
        message: "the device assessment must match device_ref and device_ref_reset",
      });
    if (event.assessments.location.primary_source !== (event.location?.primary_source ?? "none"))
      ctx.addIssue({
        code: "custom",
        path: ["assessments", "location", "primary_source"],
        message: "the location assessment must match location.primary_source",
      });
  })
  .meta({ id: "DecisionMadeV1" });
export type DecisionMadeV1 = z.infer<typeof DecisionMadeV1>;

/** Event type of {@link OperatorDeactivatedV1} on the bus and in the outbox. */
export const OPERATOR_DEACTIVATED_EVENT = "OperatorDeactivated" as const;

/**
 * `OperatorDeactivated` v1 integration event (published by `operator-auth` through
 * `operator_auth.outbox` in the transaction that deactivated the operator and revoked its
 * credentials; consumed by `iam`, which removes every console access to the operator; change
 * add-console-account-settings, design D9). Terminal: an operator is deactivated at most once.
 * `purge_after` = deactivation date + the operator's `decision_retention_years`.
 */
export const OperatorDeactivatedV1 = z
  .object({
    operator_id: z.uuid(),
    slug: z.string().min(1).max(63),
    deactivated_at: IsoDateTime,
    purge_after: IsoDateTime,
  })
  .strict()
  .meta({ id: "OperatorDeactivatedV1" });
export type OperatorDeactivatedV1 = z.infer<typeof OperatorDeactivatedV1>;
